import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
} from '@mui/material'
import { Remove } from '@mui/icons-material'
import ValidatedTextfield from '../../../../Components/ValidatedTextfield'
import { useSampleContext } from '../../../../Contexts/Modules/SampleModule/SampleModule'
import { useAddSampleContext } from '../../../../Contexts/Modules/SampleModule/AddSampleModule'
import {
  listFromKV,
  listItemsFromDataObjectArr,
} from '../../../../Functions/ListItemGenerator'
import { tsAddAnimal } from '../ValidSamplesState'
import { changeStorageSampleSelection } from './StorageSampleFns'

/**
 * A single storage sample row including Sample ID and storage metadata fields.
 * Uses code lists from context and updates parent animals state on change.
 */
function StorageSample({
  uai,
  sample,
  animals,
  setAnimals,
  sampleIndex,
  animalIndex,
  onSampleEnterTab,
}: {
  sample: any
  uai: number
  animals: tsAddAnimal
  setAnimals: any
  animalIndex: number
  sampleIndex: number
  onSampleEnterTab: any
}) {
  const {
    storageCodesQuery,
    freezingCodesQuery,
    organCodesQuery,
    descriptorCodesQuery,
  } = useSampleContext()
  const { sampleRun } = useAddSampleContext()
  const addSample = animals[uai][sampleIndex]
  const isRunType570 = sampleRun?.runInfo?.RunType === '570' || false
  return (
    <Stack
      direction={'row'}
      gap={1}
      width={1}
    >
      <Stack
        gap={1.5}
        width={1}
      >
        <Stack
          direction={'row'}
          gap={1}
          width={1}
        >
          {!isRunType570 && (
            <FormControl
              size={'small'}
              sx={{ flex: 1 }}
              required
            >
              <InputLabel>Organ</InputLabel>
              <Select
                inputProps={{ tabIndex: -1 }}
                label="Organ"
                value={addSample?.metadata?.OrganType ?? ''}
                onChange={(e) => {
                  changeStorageSampleSelection(
                    'OrganType',
                    e,
                    sampleIndex,
                    setAnimals,
                    uai,
                  )
                }}
              >
                {listFromKV(
                  organCodesQuery.data ?? [],
                  organCodesQuery.isLoading,
                )}
              </Select>
            </FormControl>
          )}
          {!isRunType570 && (
            <FormControl
              sx={{ flex: 1.5 }}
              size={'small'}
              required
            >
              <InputLabel>Storage</InputLabel>
              <Select
                inputProps={{ tabIndex: -1 }}
                label="Storage"
                value={addSample?.metadata?.StorageMedia ?? ''}
                onChange={(e) => {
                  changeStorageSampleSelection(
                    'StorageMedia',
                    e,
                    sampleIndex,
                    setAnimals,
                    uai,
                  )
                }}
              >
                {listFromKV(
                  storageCodesQuery.data ?? [],
                  storageCodesQuery.isLoading,
                )}
              </Select>
            </FormControl>
          )}
          <FormControl
            sx={{ flex: 1.5 }}
            size={'small'}
            required
          >
            <InputLabel>Freezing</InputLabel>
            <Select
              inputProps={{ tabIndex: -1 }}
              label="Freezing"
              value={addSample?.metadata?.FreezingMethod ?? ''}
              onChange={(e) => {
                changeStorageSampleSelection(
                  'FreezingMethod',
                  e,
                  sampleIndex,
                  setAnimals,
                  uai,
                )
              }}
            >
              {listFromKV(
                freezingCodesQuery.data ?? [],
                freezingCodesQuery.isLoading,
              )}
            </Select>
          </FormControl>
        </Stack>
        {!isRunType570 && (
          <Stack
            direction={'row'}
            gap={1}
          >
            <FormControl
              sx={{ flex: 3 }}
              size={'small'}
              required
            >
              <InputLabel>Sample Details</InputLabel>
              <Select
                inputProps={{ tabIndex: -1 }}
                label="Sample Details"
                value={addSample?.metadata?.SampleDetails ?? ''}
                onChange={(e) => {
                  changeStorageSampleSelection(
                    'SampleDetails',
                    e,
                    sampleIndex,
                    setAnimals,
                    uai,
                  )
                }}
              >
                {listItemsFromDataObjectArr(
                  descriptorCodesQuery.data ?? [],
                  'CodeDescription',
                  'CodeValue',
                  'CodeDescription',
                  'reqSortOrder',
                  descriptorCodesQuery.isLoading,
                )}
              </Select>
            </FormControl>
          </Stack>
        )}
        <Stack
          direction={'row'}
          gap={1}
        >
          <Box sx={{ flex: 1 }}>
            <ValidatedTextfield
              size={'small'}
              fullWidth
              onKeyDown={(e: any) => {
                if (onSampleEnterTab) {
                  onSampleEnterTab(e, animalIndex, sampleIndex)
                }
              }}
              autoFocus={animalIndex === 0 && sampleIndex === 0}
              required
              value={addSample?.sampleID ?? ''}
              label="Sample ID"
              name={String(uai)}
              onChange={(e: any) => {
                const val = e.target.value
                if (!val) {
                  setAnimals((a: any) => {
                    const newUAIsamples = a[uai]
                    newUAIsamples.splice(sampleIndex, 1, {
                      ...a[uai][sampleIndex],
                      sampleID: '',
                      valid: false,
                    })
                    return {
                      ...a,
                      [uai]: newUAIsamples,
                    }
                  })
                } else {
                  setAnimals((a: any) => {
                    const newUAIsamples = a[uai]
                    newUAIsamples.splice(sampleIndex, 1, {
                      ...a[uai][sampleIndex],
                      message: '',
                      sampleID: val,
                    })
                    return {
                      ...a,
                      [uai]: newUAIsamples,
                    }
                  })
                }
              }}
              inputProps={{ valid: String(sample?.valid) }}
            />
            <FormHelperText error>{sample?.message}</FormHelperText>
          </Box>
        </Stack>
      </Stack>
      <Button
        tabIndex={-1}
        sx={{ minWidth: 56, width: 56, minHeight: 40, height: 40 }}
        variant={'outlined'}
        color={'error'}
        onClick={() => {
          setAnimals((a: any) => {
            const newUAIsamples = a[uai]
            newUAIsamples.splice(sampleIndex, 1)
            return {
              ...a,
              [uai]: newUAIsamples,
            }
          })
        }}
      >
        <Remove />
      </Button>
    </Stack>
  )
}

export default StorageSample
